import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../hooks/useAuth';
import { useDeltakerData } from '../../../../hooks/useDeltakerData';
import { useScrollToTop } from '../../../../hooks/useScrollToTop';

import { formatName } from '../../../../utils/formatText';

import DocumentsTab from './tabs/DocumentsTab';
import InfoTab from './tabs/InfoTab';
import MealsTab from './tabs/MealsTab';
import OverviewTab from './tabs/OverviewTab';
import ProfileTab from './tabs/ProfileTab';
import ProgramTab from './tabs/ProgramTab';

import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Navigasjonsstruktur – delt mellom tab-bar (mobil) og sidebar (desktop)
const NAV_ITEMS = [
    { id: 'overview', label: 'Oversikt' },
    { id: 'program', label: 'Aktiviteter' },
    { id: 'meals', label: 'Måltider' },
    { id: 'info', label: 'Informasjon' },
    { id: 'documents', label: 'Dokumenter' },
    { id: 'profile', label: 'Profil' }
];

function DashboardPage() {
    const navigate = useNavigate();
    const scrollToTop = useScrollToTop();
    const { user, logout } = useAuth();
    const deltakerData = useDeltakerData();

    const [active, setActive] = useState('overview');
    const [activeProgramDato, setActiveProgramDato] = useState(null);

    const handleTabChange = tabId => {
        if (tabId === 'program') {
            setActiveProgramDato(null);
        }

        setActive(tabId);
        scrollToTop('smooth');
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
        scrollToTop('smooth');
    };

    return (
        <div className="minside-page minside-dashboard">
            <div className="container">
                <div className="dashboard-hero__user">
                    <p className="dashboard-hero__name">
                        Hei, {user?.firstname ? formatName(user.firstname) : 'Min side'}
                    </p>
                    <p className="dashboard-hero__sub">Du er påmeldt årets SAR61˚N.</p>
                </div>
            </div>

            <nav className="dashboard-tabbar" aria-label="Navigasjon">
                {NAV_ITEMS.map(item => (
                    <button
                        key={item.id}
                        type="button"
                        className={`dashboard-tabbar__item ${active === item.id ? 'is-active' : ''}`}
                        onClick={() => handleTabChange(item.id)}
                    >
                        {item.label}
                    </button>
                ))}
            </nav>

            <div className="container">
                <div className="dashboard-layout">
                    <aside className="dashboard-sidebar" aria-label="Sidemeny">
                        <div className="dashboard-sidebar__top">
                            <div className="dashboard-sidebar__user">
                                <p className="dashboard-sidebar__name">
                                    Hei, {user?.firstname ? formatName(user.firstname) : 'Min side'}
                                </p>
                                <p className="dashboard-sidebar__sub">
                                    Du er påmeldt årets SAR61˚N.
                                </p>
                            </div>

                            <nav className="dashboard-nav" aria-label="Sidemeny">
                                {NAV_ITEMS.map(item => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`dashboard-nav__item ${active === item.id ? 'is-active' : ''}`}
                                        onClick={() => handleTabChange(item.id)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                        </div>

                        <div className="dashboard-sidebar__bottom">
                            <p className="dashboard-sidebar__hint">
                                Har du spørsmål eller trenger hjelp med noe? Send oss en melding via{' '}
                                <a href="/om/#kontakt" target="_blank" rel="noopener noreferrer">
                                    kontaktskjemaet{' '}
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                                </a>
                                , så hjelper vi deg!
                            </p>
                            <div className="dashboard-sidebar__footer">
                                <button
                                    type="button"
                                    className="btn-outline btn-small width-100"
                                    onClick={handleLogout}
                                >
                                    Logg ut
                                </button>
                            </div>
                        </div>
                    </aside>

                    <main className="dashboard-main" aria-live="polite" aria-label="Hovedinnhold">
                        {active === 'overview' && (
                            <OverviewTab
                                data={deltakerData.data}
                                loading={deltakerData.loading}
                                onNavigate={handleTabChange}
                            />
                        )}

                        {active === 'program' && (
                            <ProgramTab
                                aktiviteter={deltakerData.data.aktiviteter}
                                aktivitetPameldApent={deltakerData.data.aktivitet_pameld_apent}
                                loading={deltakerData.loading}
                                onSaveDay={deltakerData.saveAktiviteterForDato}
                                defaultActiveDato={activeProgramDato}
                            />
                        )}

                        {active === 'meals' && (
                            <MealsTab
                                lunsj_dager={deltakerData.data.lunsj_dager}
                                middag_dager={deltakerData.data.middag_dager}
                                maltider_allergier={deltakerData.data.maltider_allergier}
                                loading={deltakerData.loading}
                                onSaveMeals={deltakerData.saveMaltider}
                            />
                        )}

                        {active === 'info' && (
                            <InfoTab onLogout={handleLogout} onNavigate={handleTabChange} />
                        )}
                        {active === 'documents' && <DocumentsTab />}
                        {active === 'profile' && <ProfileTab onLogout={handleLogout} />}
                    </main>
                </div>
            </div>
        </div>
    );
}

export default DashboardPage;
